// Časté dotazy — /caste-dotazy/ (body.in-caste-dotazy, Shoptet `type-page`).
//
// Obsah stránky (perex, sekce témat, otázky) žije v ADMINISTRACI a generuje ho
// `tools/ops/faq/sestavit-obsah.mjs` do `[itemprop=about]`:
//
//   <p class="bs-faq-perex">…</p>
//   <section class="bs-faq-sec" id="<tema>">
//     <h2>Název tématu</h2>
//     <div class="bs-pc-faq__list">
//       <details class="bs-pc-faq__item" name="bs-faq-<tema>" id="<otazka>"> … </details>
//     </div>
//   </section>
//   <script type="application/ld+json">{FAQPage}</script>
//
// Akordeon je nativní `<details>`/`<summary>` (žádný JS toggle) a sdílí třídy
// `.bs-pc-faq__*` s poradnou v popisu produktu, takže vzhled otázek nese
// main.css a tenhle modul ho neduplikuje. Bez JS zůstane stránka čitelná —
// sekce i odpovědi jsou v DOM, mizí jen nativní obal pod `html.bs-info-ready`.
//
// Modul obsah nekopíruje, PŘESOUVÁ ho (perex i sekce): kopie by nechala
// v dokumentu druhou sadu otázek a JSON-LD by popisoval skrytý obsah. JSON-LD
// skript proto zůstává v původním bloku administrace.

import { CONTACT } from './headerTools.js';
import { FAQ } from '../locale/cs/common.js';
import { el, pageHost, pageIs, markReady, buildHero, buildLinkCard, buildHelpCard, buildCtaBand } from './infoPage.js';
import { page, withPages } from './pages.js';

const SEL_SEC = 'section.bs-faq-sec';
const SEL_PEREX = 'p.bs-faq-perex';

// Lišta témat: kotva na každou sekci, text z jejího `h2`. Klik je nativní
// kotva (`#id`), nic navíc — scroll pod sticky hlavičku řeší `scroll-margin-top`.
function buildTopics(sections) {
  const nav = el('nav', 'bs-faq__topics');
  nav.setAttribute('aria-label', FAQ.topicsLabel);
  sections.forEach(function (sec) {
    const h2 = sec.querySelector('h2');
    const name = h2 ? (h2.textContent || '').replace(/\s+/g, ' ').trim() : '';
    if (!sec.id || !name) return;
    const chip = el('a', 'bs-faq__chip', name);
    chip.href = '#' + sec.id;
    nav.appendChild(chip);
  });
  return nav.children.length ? nav : null;
}

function buildSide() {
  const side = el('aside', 'bs-faq__side');
  side.appendChild(el('div', 'bs-faq__side-title', FAQ.sideTitle));
  withPages(FAQ.links).forEach(function (def) {
    side.appendChild(buildLinkCard(def));
  });
  side.appendChild(buildHelpCard({ title: FAQ.helpTitle, text: FAQ.helpText, email: CONTACT.email }));
  return side;
}

// Otázka bez `id` (starší ručně psaný obsah) by z hashe ani z odkazu nešla
// otevřít — dostane odvozené `<sekce>-<pořadí>`.
function ensureIds(sec) {
  const items = sec.querySelectorAll('details');
  for (let i = 0; i < items.length; i += 1) {
    if (!items[i].id) items[i].id = (sec.id || 'bs-faq') + '-' + (i + 1);
  }
}

// Přímý odkaz na otázku (`/caste-dotazy/#kde-je-balik`) ji musí otevřít —
// prohlížeč sám `details` neodklápí, dokud je zavřený. Platí i pro odkaz
// v rámci stránky (odpověď odkazuje na jinou otázku `#nakup-na-firmu`): to je
// jen `hashchange` bez nového načtení, init podruhé neproběhne.
function openFromHash(main) {
  const hash = (window.location.hash || '').slice(1);
  if (!hash) return;
  let node = null;
  try {
    node = main.querySelector('details[id="' + (window.CSS && CSS.escape ? CSS.escape(hash) : hash) + '"]');
  } catch (e) {
    node = null;
  }
  if (node) node.open = true;
}

export function initFaqPage() {
  if (!pageIs('faq')) return;

  const host = pageHost();
  if (!host || document.querySelector('.bs-faq')) return;

  const sections = [].slice.call(host.about.querySelectorAll(SEL_SEC));
  const perex = host.about.querySelector(SEL_PEREX);

  const root = el('div', 'bs-ip bs-faq');
  root.appendChild(
    buildHero({
      eyebrow: FAQ.eyebrow,
      title: host.h1 || null,
      perex: perex || FAQ.perexFallback, // Node se PŘESUNE, řetězec je jen fallback
      // se zapnutým chatem nahradí e-mail v heru chat; e-mail zůstává v kartě
      // bočního sloupce a v pásu na konci (chatActions)
      ctas: [
        { href: page('orderStatus'), label: FAQ.ctaOrder, icon: 'package', variant: 'primary' },
        { chat: 'faq-hero', variant: 'outline' },
        {
          href: 'mailto:' + CONTACT.email,
          label: FAQ.ctaMail + CONTACT.email,
          icon: 'mail',
          variant: 'outline',
          chatless: true,
        },
      ],
    }),
  );

  const topics = buildTopics(sections);
  if (topics) root.appendChild(topics);

  const grid = el('div', 'bs-faq__grid');
  const main = el('div', 'bs-faq__main');
  sections.forEach(function (sec) {
    ensureIds(sec);
    main.appendChild(sec); // přesun, ne kopie
  });
  grid.appendChild(main);
  grid.appendChild(buildSide());
  root.appendChild(grid);

  root.appendChild(
    buildCtaBand({
      icon: 'headset',
      title: FAQ.bandTitle,
      text: FAQ.bandText,
      actions: [
        { chat: 'faq-band', variant: 'primary' },
        { href: 'mailto:' + CONTACT.email, label: FAQ.bandMail, icon: 'mail', variant: 'primary', chatVariant: 'outline' },
      ],
    }),
  );

  host.about.classList.add('bs-ip__native');
  host.about.insertAdjacentElement('beforebegin', root);
  openFromHash(main);
  window.addEventListener('hashchange', function () {
    openFromHash(main);
  });
  markReady();
}
